import { ImageOff } from 'lucide-react'
import { useState, type CSSProperties } from 'react'

import type { SharedAlternatives, SharedEquipment } from '@/api/quote'

function ComparisonImage({ product }: { product: SharedEquipment }) {
  const [failed, setFailed] = useState(false)
  const url = product.primary_asset?.url
  return url && !failed ? (
    <img
      alt={`${product.manufacturer} ${product.model}`}
      className="h-24 w-full object-contain"
      loading="lazy"
      onError={() => setFailed(true)}
      src={url}
    />
  ) : (
    <div className="text-muted-foreground flex h-24 items-center justify-center gap-2 text-xs">
      <ImageOff aria-hidden="true" className="size-4" />
      Photo unavailable
    </div>
  )
}

function facts(product: SharedEquipment) {
  return product.specifications.map((spec) => ({
    key: spec.label.toLowerCase(),
    label: spec.label,
    value:
      spec.unit && !spec.value.toLowerCase().endsWith(spec.unit.toLowerCase())
        ? `${spec.value} ${spec.unit}`
        : spec.value,
  }))
}

/** The current item beside the alternatives Sales offered, differences first (demo `EquipmentComparison`). */
export function EquipmentComparison({ comparison }: { comparison: SharedAlternatives }) {
  const products = [comparison.current, ...comparison.alternatives]
  const all = products.map(facts)
  const facets = [...new Map(all.flat().map((fact) => [fact.key, fact.label])).entries()]
  const valueOf = (index: number, key: string) =>
    all[index]?.find((fact) => fact.key === key)?.value
  const common = facets.filter(([key]) => {
    const first = valueOf(0, key)
    return first !== undefined && products.every((_, index) => valueOf(index, key) === first)
  })
  const differences = facets.filter(([key]) => !common.some(([commonKey]) => commonKey === key))

  return (
    <div className="min-w-0 pb-3 text-sm">
      <div
        className="grid min-w-0 grid-cols-1 gap-3 md:grid-cols-[repeat(var(--columns),minmax(0,1fr))]"
        style={{ '--columns': products.length } as CSSProperties}
      >
        {products.map((product, index) => (
          <section
            aria-label={`${index === 0 ? 'On your quote' : 'Alternative'}: ${product.manufacturer} ${product.model}`}
            className="min-w-0 rounded-lg border bg-card"
            key={`${product.manufacturer}-${product.model}-${index}`}
          >
            <header className="min-w-0 p-3 [overflow-wrap:anywhere]">
              <ComparisonImage product={product} />
              <p className="text-muted-foreground mt-2 text-xs">{product.manufacturer}</p>
              <h4 className="text-sm font-semibold">{product.model}</h4>
              <p className="text-muted-foreground mt-1 text-xs leading-4">{product.title}</p>
            </header>
            <p className="border-t px-3 py-2 text-xs font-semibold">
              {index === 0 ? 'On your quote' : 'Alternative'}
            </p>
            {differences.length ? (
              <dl className="grid gap-2 border-t px-3 py-3">
                {differences.map(([key, label]) => (
                  <div className="min-w-0 [overflow-wrap:anywhere]" key={key}>
                    <dt className="text-muted-foreground text-xs">{label}</dt>
                    <dd className="font-medium">{valueOf(index, key) || 'Not specified'}</dd>
                  </div>
                ))}
              </dl>
            ) : null}
          </section>
        ))}
      </div>
      {common.length ? (
        <details className="text-muted-foreground mt-3 border-t text-xs">
          <summary className="cursor-pointer py-3 font-semibold">
            Shared specifications ({common.length})
          </summary>
          <dl className="grid gap-x-5 gap-y-2 sm:grid-cols-2">
            {common.map(([key, label]) => (
              <div className="min-w-0 [overflow-wrap:anywhere]" key={key}>
                <dt>{label}</dt>
                <dd className="text-foreground">{valueOf(0, key)}</dd>
              </div>
            ))}
          </dl>
        </details>
      ) : null}
    </div>
  )
}
